// src/pages/User/EditUser.tsx

import {
  AddTwoTone,
  CheckTwoTone,
  DeleteTwoTone,
  MoreVertTwoTone,
  PersonTwoTone,
  SaveTwoTone,
  VpnKeyTwoTone,
  ExpandMoreTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  ListSubheader,
  Tab,
  Tabs,
  Typography,
  useTheme,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material'
import countries from 'i18n-iso-countries'
import de from 'i18n-iso-countries/langs/de.json'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { UserSingle } from '@/types/domain'

import { getStorage, isValidPassword, setStorage } from '../../api'
import {
  AccUserFolderAssignments,
  AccUserSensorAssignments,
  AM,
  AMItem,
  KPage,
  KTextField,
  DlgUserPicture,
  DlgDelete,
} from '../../components'
import {
  OperatorData,
  useOperator,
  useSetSensorsByUserId,
  useUser,
  useUserApi,
} from '../../hooks'

import { UserDataMask } from './components/'

countries.registerLocale(de)

type Props = {
  PAGE_CONFIG?: string
  serviceName?: string
}

export const EditUser = ({
  PAGE_CONFIG = 'settings_user_edit',
  serviceName = 'Benutzer',
}: Props) => {
  // input parameters
  const { id: userId } = useParams()

  const { setSensorsByUserId } = useSetSensorsByUserId()

  const {
    remove: deleteUser,
    save: saveUser,
    update: updateUser,
  } = useUserApi()

  const theme = useTheme()
  const { white: color } = theme.palette

  // userId present = edit user mode
  // userId missing = create user mode
  const isEditMode = userId ? true : false

  // router
  const navigate = useNavigate()
  const URL_USER_NEW = `/users/new`

  // page config

  const [actionMenu, setActionMenu] = useState(null)
  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  // operator
  const opData: OperatorData | null = useOperator()
  const userData: UserSingle | null = useUser(Number(userId))

  // default user object shape
  const [user, setUser] = useState({
    city: '',
    contracts: [],
    country: '',
    email: '',
    email_contact: '',
    fax: '',
    firstname: '',
    folders: [],
    homepage: '',
    image_url: '',
    is_active: 1,
    lastname: '',
    password: '',
    role_id: 4, // 4=user
    sensors: [],
    street: '',
    telephone: '',
    telephone_mobile: '',
    zip: '',
    dashboard_is_active: 1,
  })

  const [isCurrent, setIsCurrent] = useState<boolean>(false)
  //const [imagePreviewUrl, setImagePreviewUrl] = useState(avatar)

  useEffect(() => {
    if (!userData?.entity_id || !opData?.id) return

    setIsCurrent(opData?.id === userData?.entity_id)
  }, [userData?.entity_id, opData?.id])

  useEffect(() => {
    if (!isEditMode || !userData) return

    const { country } = userData

    setUser(prev => ({
      ...prev,
      country: country !== null ? country : 'DEU',
      ...userData,
    }))

    // profile picture
    //setImagePreviewUrl(image_url || avatar)
  }, [userData, isEditMode])

  return (
    <>
      <DlgUserPicture
        open={config.editAvatar}
        onClose={() => setConfig(prev => ({ ...prev, editAvatar: false }))}
        id={Number(userId)}
      />

      <DlgDelete
        onCancel={() => setConfig(prev => ({ ...prev, confirmDelete: false }))}
        onConfirm={() => {
          deleteUser(Number(userId), undefined)
          setConfig(prev => ({ ...prev, confirmDelete: false }))
        }}
        open={config.confirmDelete}
      >
        <Alert severity={'warning'}>
          <AlertTitle>ACHTUNG!</AlertTitle>

          <Typography>
            {serviceName} ID: {userId} wirklich löschen?
          </Typography>
        </Alert>
      </DlgDelete>

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={serviceName}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'users'}
      >
        <AMItem
          caption={'Neu'}
          disabled={!isEditMode}
          icon={<AddTwoTone />}
          onClick={() => navigate(URL_USER_NEW)}
        />

        {isEditMode && (
          <AMItem
            caption={'Speichern'}
            icon={<SaveTwoTone />}
            onClick={() => {
              updateUser(Number(userId), user)
              setSensorsByUserId(userId, user.sensors)

              navigate(-1)
            }}
          />
        )}

        <AMItem
          disabled={!isEditMode || isCurrent}
          caption={'Löschen'}
          icon={<DeleteTwoTone />}
          onClick={() => setConfig(prev => ({ ...prev, confirmDelete: true }))}
        />

        {!isEditMode && (
          <AMItem
            caption={'Speichern'}
            icon={<SaveTwoTone />}
            onClick={() => {
              const { firstname, lastname, email, password } = user

              if (firstname && lastname && email && password) saveUser(user)

              navigate(-1)
            }}
          />
        )}

        <Divider />

        <ListSubheader>Status</ListSubheader>

        <AMItem
          disabled={isCurrent}
          caption={'Aktivieren'}
          icon={
            user.is_active ? <CheckTwoTone /> : <CheckTwoTone sx={{ color }} />
          }
          onClick={() => {
            const is_active = user.is_active === 1 ? 0 : 1
            setUser(prev => ({ ...prev, is_active }))
          }}
        />
      </AM>

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <PersonTwoTone />
          </Avatar>
        }
        title={
          isEditMode
            ? `${serviceName} ID ${userId} bearbeiten`
            : `Neuen ${serviceName} anlegen`
        }
      >
        <Collapse in={isEditMode && !userData}>
          <LinearProgress sx={{ m: 1 }} />
        </Collapse>

        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={(event, tab) => setConfig(prev => ({ ...prev, tab }))}
          sx={{ mb: 5 }}
          textColor={'primary'}
          value={config.tab}
        >
          <Tab label={'Stammdaten'} />
          <Tab label={'Zuweisungen'} />
          {!isEditMode && <Tab label={'Passwort'} />}
        </Tabs>

        {/* Stammdaten */}
        <Collapse in={config.tab === 0}>
          <UserDataMask
            user={user}
            setUser={setUser}
            isEditMode={isEditMode}
            isCurrent={isCurrent}
            onClickAvatar={() =>
              setConfig(prev => ({ ...prev, editAvatar: true }))
            }
          />
        </Collapse>

        {/* Zuweisungen */}
        <Collapse in={config.tab === 1}>
          <Collapse in={true} sx={{ mb: 2 }}>
            <Accordion expanded={true}>
              <AccordionSummary expandIcon={<ExpandMoreTwoTone />}>
                <Typography>Dashboard Creator</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user.dashboard_is_active === 1}
                      onChange={event =>
                        setUser(prev => ({
                          ...prev,
                          dashboard_is_active: event.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label={'Dashboard Sichtbarkeit'}
                />
              </AccordionDetails>
            </Accordion>
          </Collapse>
          {/* Gebäude */}
          <AccUserFolderAssignments
            config={config}
            setConfig={setConfig}
            user={user}
            setUser={setUser}
          />

          {/* Sensoren */}
          <AccUserSensorAssignments user={user} setUser={setUser} />
        </Collapse>

        {/* Password (create mode only) */}
        <Collapse in={config.tab === 2 && !isEditMode}>
          <KTextField
            error={!isValidPassword(user.password)}
            helperText={
              isValidPassword(user.password)
                ? ''
                : 'Mindestlänge 8, Zahlen, Groß- und Kleinbuchstaben erforderlich'
            }
            icon={<VpnKeyTwoTone />}
            label={'Password'}
            onChange={event =>
              setUser(prev => ({ ...prev, password: event.target.value }))
            }
            type={'password'}
          />
        </Collapse>
      </KPage>
    </>
  )
}
